/**
 * Chat API service — session + message + feedback endpoints.
 *
 * Backend chat routes are at /v1/chat/... (not /api/v1/...).
 */

import { API_CONFIG } from '../config/api';
import { getErrorMessage, ApiError, toRequestError } from '../utils/apiError';
import { getToken } from './tokenStorage';
import type { KnowledgeLevel } from './api/personaApi';
import type {
  ImportPreview,
  ImportPreviewRow,
  ProfileCard,
  ThesisCard,
  ThesisTarget,
} from './chatStream';
import type { TargetPriceCard, TargetPriceComputed } from './targetPriceCard';

export type { ProfileCard, ThesisCard, ThesisTarget, ImportPreview, ImportPreviewRow };

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type ChatSessionStatus = 'active' | 'archived' | 'deleted';

export type ChatSession = {
  id: string;
  title: string | null;
  status: ChatSessionStatus;
  /** The landing session (spec §6.1): resolved via GET sessions/main and
   * protected — the client may not rename, archive or delete it. */
  is_main?: boolean;
  archived_at: string | null;
  created_at: string;
  last_message_at: string;
  /** Unsaved-artifact badges: the (kind, target, label) projection of the
   * transcript replay — an open thesis draft, an in-flight import. Empty list
   * when nothing is in flight; absent/null on non-list readers. Payload kinds
   * (the computed target-price artifact, spec §3) additionally carry a
   * `payload` dict — their snapshot verbatim; `target_price_computed` is
   * ground truth, NOT a draft, so badge consumers must filter it out. */
  unsaved_artifacts?: {
    kind: string;
    target: string | null;
    label: string;
    payload?: Record<string, unknown>;
  }[] | null;
};

export type Citation = {
  scheme: 'http' | 'https' | 'internal';
  label: string;
  uri: string | null;
  linked: boolean;
};

export type ChatMessage = {
  id: string;
  session_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  citations: Citation[];
  job_id?: string;
  created_at: string;
  seq?: number;
  suggestions?: string[] | null;
  /** Active question awaiting the user's choice (tappable chips). `multi`
   * marks a pick-many step; absent means single-select. */
  question?: { prompt: string | null; options: string[]; multi?: boolean } | null;
  /** Structured-turn metadata (transcript-as-state: thesis_card snapshots,
   * thesis_edit patches, thesis_save receipts). */
  metadata?: Record<string, unknown> | null;
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

async function authHeaders(): Promise<HeadersInit> {
  const token = await getToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/* ------------------------------------------------------------------ */
/*  Sessions                                                           */
/* ------------------------------------------------------------------ */

export async function listSessions(
  status: 'active' | 'archived' = 'active'
): Promise<ChatSession[]> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions?status=${status}`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  const data = await resp.json();
  // A 200 whose body lacks a `sessions` array (transient gateway/proxy response
  // or malformed payload) must NOT resolve to undefined: the hook's
  // `active.data ?? []` would cache an empty list, null the selection and blank
  // the whole sidebar until a manual refresh. Throw so React Query keeps the
  // previous list and retries instead.
  if (!Array.isArray(data?.sessions)) {
    throw new Error('Malformed session list response');
  }
  return data.sessions;
}

/** Resolve-or-create the user's main (landing) session — server-side; the
 * partial unique index keeps concurrent creates to a single row. */
export async function getMainSession(): Promise<ChatSession> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/main`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

export async function createSession(title?: string): Promise<ChatSession> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ title }),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

export async function deleteSession(sessionId: string): Promise<void> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!resp.ok && resp.status !== 204) throw new Error('Failed to delete session');
}

export async function updateSession(
  sessionId: string,
  patch: { title?: string; status?: 'active' | 'archived' }
): Promise<ChatSession> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(patch),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/** Stop the session's in-flight turn (spec §6.5): best-effort gateway cancel;
 * the run's own cancelled path settles the turn and the queue then proceeds.
 * Idempotent — with no run in flight the backend answers 200 {stopping:false}. */
export async function stopSession(sessionId: string): Promise<{ stopping: boolean }> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/stop`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/* ------------------------------------------------------------------ */
/*  Messages                                                           */
/* ------------------------------------------------------------------ */

/** The server's authoritative target-price wizard fold (B3, 2026-10-31
 * staging 0100.HK): `confirmed` is the stage-key set the transition gate and
 * the save route derive, `edited` the §7 typed subset (provenance 'user').
 * Rides the NEWEST page only, and only for a session with a target-price
 * draft — the client used to replay both from whichever messages it had paged
 * in (a 20-row window against the server's 200), so a mid-wizard reload could
 * report a stage the server had already advanced past. */
export type TargetPriceWizardState = {
  confirmed: string[];
  edited: string[];
};

export type MessagePage = {
  messages: ChatMessage[];
  /** True when older messages exist beyond this page (pagination only). */
  has_more: boolean;
  /** Present ONLY when the server actually folded a wizard state for this
   * session. `undefined` means UNKNOWN — a backend without B3, a fold the
   * server degraded away after a failed probe, or a malformed payload — and
   * the sheet then arms Compute on value presence alone. Collapsing unknown to
   * "nothing confirmed" (review #1) disabled Compute permanently across a
   * mixed-version window, with the confirm-all button that used to recover it
   * deleted by this same PR. A present-but-empty `{confirmed: []}` is the real
   * "draft exists, no stage confirmed yet" and still gates strictly. */
  target_price_state?: TargetPriceWizardState;
};

export async function listMessages(
  sessionId: string,
  opts?: { limit?: number; beforeSeq?: number }
): Promise<MessagePage> {
  const params = new URLSearchParams();
  if (opts?.limit != null) params.set('limit', String(opts.limit));
  if (opts?.beforeSeq != null) params.set('before_seq', String(opts.beforeSeq));
  const qs = params.toString();
  const resp = await fetch(
    `${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/messages${qs ? `?${qs}` : ''}`,
    { headers: await authHeaders() }
  );
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  const data = await resp.json();
  // Deliberately NOT shape-guarded like listSessions: a malformed 200 here
  // degrades to an empty page that self-heals on the next poll/WS refetch,
  // whereas a blanked session list nulls the sidebar selection until a manual
  // refresh. Guarding this too would trade a transient flicker for a thrown
  // error on every hiccup; the asymmetry is intentional (review, PR #382).
  // The wizard fold IS checked, though narrowly: it is the confirmed-key set
  // the compute gate reads, so a non-array there must not poison a Set. It
  // degrades to ABSENT (unknown), not to an empty set — see MessagePage.
  const state = data.target_price_state;
  return {
    messages: data.messages ?? [],
    has_more: data.has_more ?? false,
    ...(state && Array.isArray(state.confirmed) && Array.isArray(state.edited)
      ? {
          target_price_state: {
            confirmed: state.confirmed as string[],
            edited: state.edited as string[],
          },
        }
      : {}),
  };
}

/** POST /messages result. When a turn is already in flight the backend
 * queues the message instead of running it and answers
 * `{queued: true, message}` — the message is still persisted (it appears in
 * the transcript immediately); a plain ChatMessage means the turn started
 * (or ran) right away. */
export type PostMessageResult = ChatMessage | { queued: true; message: ChatMessage };

export async function postMessage(sessionId: string, content: string): Promise<PostMessageResult> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/messages`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ content }),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/* ------------------------------------------------------------------ */
/*  Feedback                                                           */
/* ------------------------------------------------------------------ */

export async function postFeedback(
  sessionId: string,
  messageId: string,
  thumbs: 'up' | 'down',
  note?: string
): Promise<void> {
  const resp = await fetch(
    `${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/messages/${messageId}/feedback`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ thumbs, note }),
    }
  );
  if (!resp.ok && resp.status !== 204) throw new Error('Failed to submit feedback');
}

/* ------------------------------------------------------------------ */
/*  Thesis builder (direct edits + confirm-to-save, spec §3.6/§4.3)     */
/* ------------------------------------------------------------------ */

/** Sheet edit → structured transcript message. No gateway call: direct
 * manipulation is instant; the agent sees the edit next turn. */
export async function postThesisEdit(
  sessionId: string,
  fields: Record<string, unknown>
): Promise<{ card: ThesisCard }> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/thesis-edits`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ fields }),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/** 409 from thesis-save: a position AND a watchlist item both exist for the
 * card's symbol, so the save needs an explicit target. Carries both candidate
 * ids from the response body so the caller can offer a chooser (spec §3.6). */
export class ThesisSaveConflictError extends Error {
  readonly positionId: number | null;
  readonly watchlistItemId: number | null;

  constructor(detail: Record<string, unknown>) {
    super(
      typeof detail.message === 'string'
        ? detail.message
        : 'Both a position and a watchlist item exist for this symbol'
    );
    this.name = 'ThesisSaveConflictError';
    this.positionId = typeof detail.position_id === 'number' ? detail.position_id : null;
    this.watchlistItemId =
      typeof detail.watchlist_item_id === 'number' ? detail.watchlist_item_id : null;
  }

  /** True when the 409 carried at least one candidate to offer. */
  get hasCandidates(): boolean {
    return this.positionId != null || this.watchlistItemId != null;
  }
}

/** Confirm-to-save: pure REST write into PositionThesis — no LLM in the
 * commit path. Omit *target* to let the backend resolve/create one. */
export async function postThesisSave(
  sessionId: string,
  card: ThesisCard,
  target?: ThesisTarget
): Promise<{ thesis_id: number }> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/thesis-save`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ card, ...(target ? { target } : {}) }),
  });
  if (!resp.ok) {
    const body: unknown = await resp.json().catch(() => null);
    const detail = (body as { detail?: unknown } | null)?.detail;
    if (
      resp.status === 409 &&
      typeof detail === 'object' &&
      detail !== null &&
      !Array.isArray(detail)
    ) {
      throw new ThesisSaveConflictError(detail as Record<string, unknown>);
    }
    throw new Error(
      body
        ? getErrorMessage(body as Parameters<typeof getErrorMessage>[0])
        : 'Failed to save thesis'
    );
  }
  return resp.json();
}

/** ✕ on the draft bar: persist the dismissal as a system receipt in the
 * transcript so a session reopen does not resurrect the bar. Fire-and-forget
 * on the client — a failure only means the bar may re-arm on the next reload
 * (the local hide is not reverted). */
export async function postThesisDismiss(sessionId: string): Promise<void> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/thesis-dismiss`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
}

/* ------------------------------------------------------------------ */
/*  Target-price builder (Part 2, Task 12) — exact twins of the thesis  */
/*  trio above, on the targetPriceCard domain: transcript-as-state,     */
/*  no LLM in any commit path, compute is the only new route.           */
/* ------------------------------------------------------------------ */

/** Sheet edit → structured transcript message. No gateway call: direct
 * manipulation is instant; the agent sees the edit next turn. `accept` is the
 * stage-key list the edit covers (acceptKeysForEdit) and `edited` the §7
 * typed subset (editedKeysForEdit — the keys the user actually typed or
 * changed) — the server persists BOTH lists in the target_price_accept stamp
 * (`{keys, edited}`, spec §7 provenance protocol) and, when the accept list
 * is non-empty, drains the staged advance. Returns the server-folded card
 * (the client's optimistic applyEdit merge is replaced by it — or rolled
 * back to the pre-edit snapshot when this POST fails). */
export async function postTargetPriceEdit(
  sessionId: string,
  fields: Record<string, unknown>,
  accept: string[] = [],
  edited: string[] = []
): Promise<{ card: TargetPriceCard }> {
  const resp = await fetch(
    `${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/target-price-edits`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ fields, accept, edited }),
    }
  );
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/** Fold the draft drivers through the DCF kernel server-side (spec §12 —
 * compute and save share ONE server-side fold; the client never supplies
 * computed values). Pure function of the body: no DB write, no LLM. 422
 * details (unbounded wacc-vs-tg, probability drift) surface through the same
 * getErrorMessage shape as the thesis trio. */
export async function postTargetPriceCompute(
  sessionId: string,
  card: TargetPriceCard
): Promise<TargetPriceComputed> {
  const resp = await fetch(
    `${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/target-price-compute`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ card }),
    }
  );
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

export interface TargetPriceSavePayload {
  card: TargetPriceCard;
  /** {driver_key: 'user' | 'ai_accepted'} — the §15 audit trail from
   * provenanceOf(card, confirmed). */
  provenance: Record<string, 'user' | 'ai_accepted'>;
  /** Omit to let the backend auto-resolve by the card's symbol (thesis-save
   * twin; unlike thesis there is NO watchlist auto-create — an unresolvable
   * target 4xxs, and a both-exists symbol 409s with candidate ids). */
  target?: ThesisTarget;
}

/** Confirm-to-save: pure REST write into TargetPriceScenario. The backend
 * recomputes the kernel fold server-side (the row stores OUR numbers, never
 * client-submitted ones) and re-runs the thesis hard-gate on the card symbol
 * — the 422 no-thesis detail rides back through getErrorMessage exactly like
 * every other failure, so the sheet can show it inline. A 409 both-exists
 * reuses ThesisSaveConflictError (identical wire detail: position_id +
 * watchlist_item_id). */
export async function postTargetPriceSave(
  sessionId: string,
  payload: TargetPriceSavePayload
): Promise<{ id: number }> {
  const resp = await fetch(
    `${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/target-price-save`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(payload),
    }
  );
  if (!resp.ok) {
    const body: unknown = await resp.json().catch(() => null);
    const detail = (body as { detail?: unknown } | null)?.detail;
    if (
      resp.status === 409 &&
      typeof detail === 'object' &&
      detail !== null &&
      !Array.isArray(detail)
    ) {
      throw new ThesisSaveConflictError(detail as Record<string, unknown>);
    }
    throw new Error(
      body
        ? getErrorMessage(body as Parameters<typeof getErrorMessage>[0])
        : 'Failed to save target price'
    );
  }
  return resp.json();
}

/** ✕ on the target-price bar: persist the dismissal receipt so a session
 * reopen does not resurrect the bar (the backend transcript replay keeps the
 * card as the agent's ground truth — the marker is UI-only, exactly the
 * thesis-dismiss doctrine). Fire-and-forget on the client. */
export async function postTargetPriceDismiss(sessionId: string): Promise<{ ok: boolean }> {
  const resp = await fetch(
    `${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/target-price-dismiss`,
    {
      method: 'POST',
      headers: await authHeaders(),
    }
  );
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/** Confirm-to-save for the build_profile flow (revamp T24): pure REST write —
 * one portfolio_theses style/risk version + one user_personas persona version
 * (source='onboarding') in one backend transaction, then the profile_saved
 * receipt. No LLM in the commit path. */
export interface ProfileSaveResult {
  thesis_version_id: number;
  persona: { knowledge_level: string; source: string; updated_at?: string } | null;
}

export async function postProfileSave(
  sessionId: string,
  card: ProfileCard
): Promise<ProfileSaveResult> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/profile-save`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ card }),
  });
  if (!resp.ok) {
    const body: unknown = await resp.json().catch(() => null);
    throw new Error(
      body
        ? getErrorMessage(body as Parameters<typeof getErrorMessage>[0])
        : 'Failed to save profile'
    );
  }
  return resp.json();
}

/** Sheet chip tap → structured transcript message (the /target-price-edits
 * twin): instant, no LLM — the agent sees the folded card (including this
 * write) through the PROFILE CARD ground-truth line next turn. `accept` is
 * the dimension list the tap confirms and `edited` the changed subset; the
 * server vocabulary-filters and value-gates BOTH before stamping
 * `profile_accept`. Returns the server-folded card. */
export async function postProfileEdit(
  sessionId: string,
  fields: Record<string, unknown>,
  accept: string[] = [],
  edited: string[] = []
): Promise<{ card: ProfileCard }> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/profile-edits`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ fields, accept, edited }),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/** ✕ on the profile bar: persist the dismissal receipt so a session reopen
 * does not resurrect the bar (thesis/target-price dismiss twin; the backend
 * replay keeps the card as the agent's ground truth — UI-only marker). */
export async function postProfileDismiss(sessionId: string): Promise<{ ok: boolean }> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/profile-dismiss`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/* ------------------------------------------------------------------ */
/*  Persona evolution (revamp T25) — the chip-confirmed proposal's      */
/*  commit path: no LLM, the agent never writes the persona.            */
/* ------------------------------------------------------------------ */

export type PersonaConfirmResult = {
  knowledge_level: string;
  source: string;
  updated_at: string;
};

/** Commit a picked level-change proposal: one user_personas version
 * (source='chat_confirmed') plus the persona_confirmed receipt in the
 * transcript. The conversational turn itself proceeds normally. */
export async function postPersonaConfirm(
  sessionId: string,
  knowledgeLevel: KnowledgeLevel
): Promise<PersonaConfirmResult> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/persona-confirm`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ knowledge_level: knowledgeLevel }),
  });
  if (!resp.ok) {
    const body: unknown = await resp.json().catch(() => null);
    throw new Error(
      body
        ? getErrorMessage(body as Parameters<typeof getErrorMessage>[0])
        : 'Failed to update persona'
    );
  }
  return resp.json();
}

/* ------------------------------------------------------------------ */
/*  Import sheet (direct edits + save/undo/dismiss, spec §4.5) — the    */
/*  twins of the thesis set above: no LLM, transcript is the store.     */
/* ------------------------------------------------------------------ */

/** Allowlisted keys an import row edit may touch (mirrors the backend's
 * EDITABLE_ROW_FIELDS; `null` = explicit clear). */
export type ImportRowPatch = Partial<{
  symbol: string | null;
  quantity: number | null;
  price: number | null;
  currency: string | null;
  date: string | null;
  instrument_id: number | null;
  resolution_default: 'add' | 'replace' | null;
}>;

/** Sheet edit → structured transcript message. No gateway call: direct
 * manipulation is instant; the agent sees the edit next turn. Returns the
 * replay-folded preview (null once a terminal receipt ended the draft). */
export async function postImportEdit(
  sessionId: string,
  rowId: string,
  patch: ImportRowPatch,
  include?: boolean
): Promise<{ preview: ImportPreview | null }> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/import-edits`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ row_id: rowId, patch, ...(include !== undefined ? { include } : {}) }),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/** One row of the sheet's FINAL state for save: include/override flags plus
 * the flattened final values (omitted/null = the preview's value wins). */
export interface ImportSaveRow {
  row_id: string;
  include?: boolean;
  resolution_action?: 'add' | 'replace' | null;
  symbol?: string | null;
  quantity?: number | null;
  price?: number | null;
  currency?: string | null;
  /** ISO YYYY-MM-DD (the backend save gate rejects anything else). */
  date?: string | null;
  instrument_id?: number | null;
}

export interface ImportSaveOutcome {
  positions_created: number;
  trades_created: number;
  skipped: string[];
}

/** Confirm-to-save: pure REST write through the shared save path (FIFO-safe,
 * per-row savepoints) — no LLM in the commit path. Every created trade
 * carries the chat-import marker, so postImportUndo can reverse this group. */
export async function postImportSave(
  sessionId: string,
  groupId: number,
  rows: ImportSaveRow[]
): Promise<ImportSaveOutcome> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/import-save`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ group_id: groupId, rows }),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/** Reverse a save: deletes every trade the group created (found via the
 * chat-import marker), FIFO recalculated per symbol server-side. */
export async function postImportUndo(
  sessionId: string,
  groupId: number
): Promise<{ group_id: number; trades_deleted: number }> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/import-undo`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ group_id: groupId }),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/** ✕ on the review bar / cancel of a pending import → persisted dismissal
 * receipt. Terminal for replay: the draft is over until a fresh preview
 * snapshot lands. */
export async function postImportDismiss(
  sessionId: string,
  groupId: number
): Promise<{ ok: boolean }> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/import-dismiss`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ group_id: groupId }),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/** ✕ on the stale-coverage notice (research-freshness spec §C.5). The receipt
 * is version-scoped — `{ticker, version}` against the coverage row's version —
 * so a later refresh that bumps the version re-arms the offer instead of one
 * tap muting the ticker forever. `ticker` must be the card's canonical form
 * (`0175.HK`, not the `175.HK` the user typed) or the server's match never
 * fires. */
export async function postRefreshDismiss(
  sessionId: string,
  ticker: string,
  version: number
): Promise<{ ok: boolean }> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/sessions/${sessionId}/refresh-dismiss`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ ticker, version }),
  });
  if (!resp.ok) throw new Error(getErrorMessage(await resp.json()));
  return resp.json();
}

/* ------------------------------------------------------------------ */
/*  Consent                                                            */
/* ------------------------------------------------------------------ */

export async function getConsent(): Promise<{ accepted: boolean }> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/consent`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw await toRequestError(resp);
  return resp.json();
}

export async function acceptConsent(): Promise<void> {
  const resp = await fetch(`${API_CONFIG.baseURL}/v1/chat/consent`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!resp.ok && resp.status !== 204) throw await toRequestError(resp);
}
